/* eslint-disable @typescript-eslint/return-await */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { unionBy } from 'lodash';

import organizationAdminService from '../services/organizationAdminService';
import teamService from '../services/teamService';
import { editTeam, inviteUser } from './actions/teamActions';
import { changeUserPermissionLevel, deleteInvites, deleteUsersFromOrganization } from './teamSlice';

export const listSubOrganizations = createAsyncThunk('organizationAdmin/listSubOrganizations', async ({ superOrgId } = {}) => {
  return await organizationAdminService.listSubOrganizations(superOrgId);
});

export const listAllOrganizations = createAsyncThunk(
  'organizationAdmin/listAllOrganizations',
  async ({ superOrgId, searchQuery, sortBy } = {}) => {
    return await organizationAdminService.listAllOrganizations(superOrgId, searchQuery, sortBy);
  }
);
export const listAllOrganizationsNext = createAsyncThunk('organizationAdmin/listAllOrganizationsNext', async ({ url } = {}) => {
  return await organizationAdminService.listAllOrganizations(null, null, null, url);
});

export const createSubOrganization = createAsyncThunk(
  'organizationAdmin/createSubOrganization',
  async ({ superOrgId, name, description, administrators, creators }, { rejectWithValue }) => {
    try {
      const createdSubOrganization = await organizationAdminService.createSubOrganization(superOrgId, {
        name,
        description,
        administrators,
        creators
      });

      return { ...createdSubOrganization };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const updateSubOrganization = createAsyncThunk('organizationAdmin/updateSubOrganization', async (body) => {
  const updated = await organizationAdminService.updateSubOrganization(body.id, body);

  return { ...updated };
});

export const deleteSubOrganization = createAsyncThunk('organizationAdmin/deleteSubOrganization', async ({ orgId, newOrgId }) => {
  const res = await organizationAdminService.deleteSubOrganization(orgId);

  return orgId;
});

export const getSelectedTeamUsers = createAsyncThunk(
  'organizationAdmin/getSelectedTeamUsers',
  async ({ id, nameParam, sortParam, annotateCountInfo }) => {
    const teammates = await teamService.getTeamUsers(id, nameParam, sortParam, annotateCountInfo);

    return teammates;
  }
);

export const getAllUsersFromOrganization = createAsyncThunk(
  'organizationAdmin/getAllUsersFromOrganization',
  async ({ id, searchQuery, role, sortBy }) => {
    return await organizationAdminService.listAllUsersFromOrg({
      searchQuery,
      id,
      role,
      sortBy
    });
  }
);

export const getNextUsersFromOrg = createAsyncThunk('organizationAdmin/getNextUsersFromOrg', async ({ url }) => {
  return await organizationAdminService.listAllUsersFromOrg({ url });
});

export const getAllInvitesFromOrg = createAsyncThunk(
  'organizationAdmin/listAllInvitesForSuperOrg',
  async ({ id, searchQuery, sortParam }) => {
    const pendingInvites = await organizationAdminService.listAllInvitesForSuperOrg(id, searchQuery, sortParam);

    return pendingInvites;
  }
);

export const getSelectedTeamInvites = createAsyncThunk('organizationAdmin/getSelectedTeamInvites', async ({ id, emailParam }) => {
  const invites = await teamService.getTeamInvites(id, emailParam);

  return invites;
});

export const resendInvite = createAsyncThunk('organizationAdmin/resendInvite', async ({ inviteCode }) => {
  await teamService.resendInvite(inviteCode);
  return inviteCode;
});

export const deleteTeamsFromOrg = createAsyncThunk('organizationAdmin/deleteTeamsFromOrg', async ({ orgId, teamIds }) => {
  const res = await organizationAdminService.deleteTeamsFromOrg(orgId, teamIds);

  return res;
});

export const addUsersToTeam = createAsyncThunk('organizationAdmin/addUsersToTeam', async ({ teamId, userIds }, { rejectWithValue }) => {
  try {
    const res = await organizationAdminService.addUsersToTeam(teamId, userIds);

    return res;
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const moveUsersToTeam = createAsyncThunk('organizationAdmin/moveUsersToTeam', async ({ teamId, users }, { rejectWithValue }) => {
  try {
    const res = await organizationAdminService.moveUsersToTeam(teamId, users);

    return res;
  } catch (error) {
    return rejectWithValue(error);
  }
});
export const removeUsersToTeams = createAsyncThunk(
  'organizationAdmin/removeUsersToTeams',
  async ({ superOrgId, users }, { rejectWithValue }) => {
    try {
      const res = await organizationAdminService.removeUsersToTeams(superOrgId, users);

      return res;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

const INITIAL_STATE = {
  isLoading: false,
  isLoadingNext: false,
  isError: false,
  errorMessage: null,
  teams: null,
  totalUsers: 0,
  usersAndTeams: [],
  organizations: [],
  subOrganizations: [],
  totalOrganizations: 0,
  totalPages: 0,
  totalScribes: 0,
  searchQuery: '',
  loadedAll: false,
  allOrganizations: [],
  selectedTeamUsers: [],
  selectedTeamInvites: [],
  loadingSelectedTeamUsers: false,
  loadingSuperorgUsers: false,
  isLoadingNextSuperorgUsers: false,
  loadedAllUsersFromSuperOrg: false,
  totalUsersFromSuperorg: 0,
  totalUsersFromSuperorgExcludingScribeUsers: 0,
  loadingSelectedTeamInvites: false,
  loadingOrgUsers: false,
  pendingInvites: [],
  loadingPendingInvites: false,
  nextUrlUsersAndTeams: null,
  nextUrlAllOrganizations: null
};

const organizationAdminSlice = createSlice({
  name: 'organizationAdmin',
  initialState: { ...INITIAL_STATE },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(listSubOrganizations.fulfilled, (state, action) => {
        state.subOrganizations = action.payload;
        state.isLoading = false;
        state.isError = false;
      })
      .addCase(listSubOrganizations.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
      })
      .addCase(listSubOrganizations.rejected, (state) => {
        state.isLoading = false;
        state.isError = true;
      })
      .addCase(createSubOrganization.fulfilled, (state, action) => {
        const subOrganization = action.payload;
        const updatedOrganizations = [...state.organizations, subOrganization];
        const updatedAllOrganizations = [...state.allOrganizations, subOrganization];

        state.organizations = unionBy(state.organizations, updatedOrganizations, 'id');
        state.allOrganizations = unionBy(state.allOrganizations, updatedAllOrganizations, 'id');
        state.totalOrganizations += 1;
        state.isLoading = false;
        state.isError = false;
      })
      .addCase(createSubOrganization.rejected, (state) => {
        // state.isLoading = false;
        // state.isError = true;
      })
      .addCase(createSubOrganization.pending, (state) => {
        // state.isLoading = true;
        // state.isError = false;
      })
      .addCase(updateSubOrganization.fulfilled, (state, action) => {
        const subOrganization = action.payload;

        state.organizations = state.organizations.map((org) => {
          if (org.id === subOrganization.id) {
            return { ...org, ...subOrganization };
          }
          return org;
        });
        state.allOrganizations = state.allOrganizations.map((org) => {
          if (org.id === subOrganization.id) {
            return { ...org, ...subOrganization };
          }
          return org;
        });
        state.isLoading = false;
        state.isError = false;
      })
      .addCase(updateSubOrganization.rejected, (state) => {
        state.isLoading = false;
        state.isError = true;
      })
      .addCase(updateSubOrganization.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
      })
      .addCase(deleteSubOrganization.fulfilled, (state, action) => {
        const subOrganizationId = action.meta.arg.orgId;
        const updatedOrganizations = state.organizations.filter((org) => org.id !== subOrganizationId);
        const { knowledge_page_document_total } = action.payload;

        state.organizations = updatedOrganizations;
        state.totalScribes -= 1;
        state.totalPages = knowledge_page_document_total;

        // state.isLoading = false;
        // state.isError = false;
      })
      .addCase(deleteSubOrganization.pending, (state) => {
        // state.isLoading = true;
        // state.isError = false;
      })
      .addCase(deleteSubOrganization.rejected, (state) => {
        // state.isLoading = false;
        // state.isError = true;
      })
      .addCase(listAllOrganizations.fulfilled, (state, action) => {
        state.allOrganizations = action.payload.results;
        state.totalOrganizations = action.payload.count;
        state.nextUrlAllOrganizations = action.payload.next;
        // state.isLoading = true;
        // state.isError = false;
      })
      .addCase(listAllOrganizations.pending, (state) => {
        // state.isLoading = false;
        // state.isError = true;
      })
      .addCase(listAllOrganizations.rejected, (state) => {
        // state.isLoading = false;
        // state.isError = true;
      })
      .addCase(listAllOrganizationsNext.pending, (state) => {
        state.isLoadingNext = true;
        state.isError = false;
      })
      .addCase(listAllOrganizationsNext.fulfilled, (state, action) => {
        state.allOrganizations = [...state.allOrganizations, ...action.payload.results];
        state.nextUrlAllOrganizations = action.payload.next;
        state.isLoading = false;
        state.isLoadingNext = false;
        state.isError = false;
      })
      .addCase(getSelectedTeamUsers.fulfilled, (state, action) => {
        state.selectedTeamUsers = action.payload.results;
        state.loadingSelectedTeamUsers = false;
        state.isError = false;
      })
      .addCase(getSelectedTeamUsers.pending, (state) => {
        state.selectedTeamUsers = [];
        state.loadingSelectedTeamUsers = true;
        state.isError = true;
      })
      .addCase(getSelectedTeamUsers.rejected, (state) => {
        state.loadingSelectedTeamUsers = false;
        // state.isError = true;
      })
      .addCase(getAllUsersFromOrganization.fulfilled, (state, action) => {
        state.usersAndTeams = action.payload.results;
        state.nextUrlUsersAndTeams = action.payload.next;
        state.totalUsersFromSuperorg = action.payload.count;
        state.totalUsersFromSuperorgExcludingScribeUsers = action.payload.users_total_excluding_scribe_users;
        state.loadingSuperorgUsers = false;
      })
      .addCase(getAllUsersFromOrganization.pending, (state) => {
        state.loadingSuperorgUsers = true;
      })
      .addCase(getAllUsersFromOrganization.rejected, (state) => {
        state.usersAndTeams = false;
        state.totalUsersFromSuperorg = false;
        state.totalUsersFromSuperorgExcludingScribeUsers = false;
        state.loadingSuperorgUsers = false;
      })
      .addCase(getNextUsersFromOrg.pending, (state) => {
        state.isLoadingNextSuperorgUsers = true;
      })
      .addCase(getNextUsersFromOrg.fulfilled, (state, action) => {
        state.usersAndTeams = [...state.usersAndTeams, ...action.payload.results];
        state.nextUrlUsersAndTeams = action.payload.next;
        state.loadingSuperorgUsers = false;
        state.isLoadingNextSuperorgUsers = false;
      })
      .addCase(getNextUsersFromOrg.rejected, (state) => {
        state.isLoadingNextSuperorgUsers = false;
      })
      .addCase(getSelectedTeamInvites.fulfilled, (state, action) => {
        state.selectedTeamInvites = action.payload;
        state.loadingSelectedTeamInvites = false;
        // state.isError = false;
      })
      .addCase(getSelectedTeamInvites.pending, (state) => {
        state.selectedTeamInvites = [];
        state.loadingSelectedTeamInvites = true;
        // state.isError = true;
      })
      .addCase(getSelectedTeamInvites.rejected, (state) => {
        state.loadingSelectedTeamInvites = false;
        // state.isError = true;
      })
      .addCase(inviteUser.fulfilled, (state, action) => {
        state.selectedTeamInvites = [...state.selectedTeamInvites, action.payload];
        // state.isError = false;
        // state.errorMessage = null;
      })
      .addCase(inviteUser.pending, (state) => {
        // state.isError = false;
        // state.errorMessage = null;
      })
      .addCase(inviteUser.rejected, (state, action) => {
        // state.isError = true;
        // state.errorMessage = action.payload;
      })
      .addCase(changeUserPermissionLevel.fulfilled, (state, action) => {
        const userOrgPermission = action.payload;

        state.usersAndTeams = state.usersAndTeams.map((user) => {
          const updatedUser = { ...user };

          if (updatedUser.id === userOrgPermission.id) {
            updatedUser.teams = updatedUser.teams.map((team) => {
              let updatedTeam = { ...team };

              if (team.team_id === userOrgPermission.organization.id) {
                updatedTeam = { ...updatedTeam, permission_level: userOrgPermission.permission_level };
              }
              return updatedTeam;
            });
          }
          return updatedUser;
        });

        state.isLoading = false;
        state.isError = false;
      })
      .addCase(deleteUsersFromOrganization.fulfilled, (state, action) => {
        const { userIds, organizationId } = action.payload;

        state.selectedTeamUsers = state.selectedTeamUsers.filter((user) => !userIds.includes(user.id));
        state.usersAndTeams = state.usersAndTeams.map((user) => {
          if (userIds.includes(user.id)) {
            const updatedUser = { ...user };

            updatedUser.teams = updatedUser.teams.filter((team) => team.team_id !== organizationId);
            return updatedUser;
          }
          return user;
        });
        state.organizations = state.organizations.map((org) => {
          if (org.id === organizationId) {
            const updatedOrg = { ...org };

            updatedOrg.user_count -= 1;
            return updatedOrg;
          }
          return org;
        });
        // state.isLoading = false;
        // state.isError = true;
      })
      .addCase(deleteInvites.fulfilled, (state, action) => {
        const inviteCodes = action.payload;

        state.selectedTeamInvites = state.selectedTeamInvites.filter((invite) => !inviteCodes.includes(invite.invite_code));
        state.pendingInvites = state.pendingInvites.filter((invite) => !inviteCodes.includes(invite.invite_code));
        // state.isLoading = false;
        // state.isError = true;
      })
      .addCase(resendInvite.fulfilled, (state, action) => {
        // state.isError = false;
        // state.errorMessage = null;
      })
      .addCase(resendInvite.pending, (state) => {
        // state.isError = false;
        // state.errorMessage = null;
      })
      .addCase(resendInvite.rejected, (state, action) => {
        // state.isError = true;
        // state.errorMessage = action.payload;
      })
      .addCase(getAllInvitesFromOrg.fulfilled, (state, action) => {
        state.pendingInvites = action.payload;
        state.loadingPendingInvites = false;
      })
      .addCase(getAllInvitesFromOrg.pending, (state) => {
        state.loadingPendingInvites = true;
      })
      .addCase(getAllInvitesFromOrg.rejected, (state) => {
        state.pendingInvites = false;
        state.loadingPendingInvites = false;
      })
      .addCase(deleteTeamsFromOrg.fulfilled, (state, action) => {
        const { teamIds } = action.meta.arg;

        state.allOrganizations = state.allOrganizations.filter((org) => !teamIds.includes(org.id));
        state.totalOrganizations -= teamIds.length;
        state.isLoading = false;
      })
      .addCase(deleteTeamsFromOrg.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteTeamsFromOrg.rejected, (state) => {
        state.organizations = false;
        state.isLoading = false;
      })
      .addCase(addUsersToTeam.fulfilled, (state, action) => {
        const organizationToMoveUsers = action.payload.organization; // new organization where user/s were moved to
        const newOrg = {
          team_id: organizationToMoveUsers.id,
          team_color: organizationToMoveUsers.color,
          team_name: organizationToMoveUsers.name,
          total_team_documents: 0,
          permission_level: action.payload.permission_level
        };

        state.usersAndTeams = state.usersAndTeams.map((user) => {
          const updatedUser = { ...user };
          const payloadUser = action.payload.users.find((u) => u.id === user.id);

          if (payloadUser) {
            updatedUser.teams = [newOrg, ...updatedUser.teams];
          }
          return updatedUser;
        });
        state.isLoading = false;
      })
      .addCase(addUsersToTeam.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(addUsersToTeam.rejected, (state) => {
        state.organizations = false;
        state.isLoading = false;
      })
      .addCase(moveUsersToTeam.fulfilled, (state, action) => {
        const organizationToMoveUsers = action.payload.organization; // new organization where user/s were moved to
        const newOrg = {
          team_id: organizationToMoveUsers.id,
          team_color: organizationToMoveUsers.color,
          team_name: organizationToMoveUsers.name,
          total_team_documents: 0,
          permission_level: action.payload.permission_level
        };

        state.usersAndTeams = state.usersAndTeams.map((user) => {
          const updatedUser = { ...user };
          const payloadUser = action.payload.users.find((u) => u.user_id === user.id);

          if (payloadUser) {
            const payloadUserOrgs = payloadUser.organization_ids;
            const teamsToRemove = updatedUser.teams.filter((team) => payloadUserOrgs.includes(team.team_id));
            // count documents from teams to remove
            const countDocuments = teamsToRemove.reduce((sum, curr) => sum + curr.total_team_documents, 0);

            updatedUser.teams = [{ ...newOrg, total_team_documents: countDocuments }, ...updatedUser.teams];
            updatedUser.teams = updatedUser.teams.filter((team) => !payloadUserOrgs.includes(team.team_id));
          }
          return updatedUser;
        });
        state.isLoading = false;
      })
      .addCase(moveUsersToTeam.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(moveUsersToTeam.rejected, (state, action) => {
        state.isError = true;
        state.errorMessage = action.payload.users;
        state.organizations = false;
        state.isLoading = false;
      })
      .addCase(removeUsersToTeams.fulfilled, (state, action) => {
        let usersWithOutTeams = [];

        state.usersAndTeams = state.usersAndTeams.map((user) => {
          const updatedUser = { ...user };
          const payloadUser = action.payload.users.find((u) => u.user_id === user.id);

          if (payloadUser) {
            const payloadUserOrgs = payloadUser.organization_ids;

            updatedUser.teams = updatedUser.teams.filter((team) => !payloadUserOrgs.includes(team.team_id));
          }
          if (updatedUser.teams.length === 0) {
            usersWithOutTeams = [updatedUser.id, ...usersWithOutTeams];
          }

          return updatedUser;
        });
        if (usersWithOutTeams.length > 0) {
          state.usersAndTeams = state.usersAndTeams.filter((user) => !usersWithOutTeams.includes(user.id));
          state.totalUsers -= usersWithOutTeams.length;
        }
        state.isLoading = false;
      })
      .addCase(removeUsersToTeams.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(removeUsersToTeams.rejected, (state, action) => {
        state.isError = true;
        state.errorMessage = action.payload.users;
        state.organizations = false;
        state.isLoading = false;
      })
      .addCase(editTeam.fulfilled, (state, action) => {
        const updatedTeam = action.payload;

        state.subOrganizations = state.subOrganizations.map((org) => {
          if (org.id === updatedTeam.id) {
            return updatedTeam;
          }
          return org;
        });
        state.allOrganizations = state.allOrganizations.map((org) => {
          if (org.id === updatedTeam.id) {
            return updatedTeam;
          }
          return org;
        });

        state.isLoading = false;
      })
      .addCase('signOutGlobally', () => ({ ...INITIAL_STATE }));
  }
});

export default organizationAdminSlice.reducer;
